import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { useAuthInit } from '@/hooks/useAuth'
import { usePush } from '@/hooks/usePush'
import { useAuthStore } from '@/store/auth'
import { AuthGuard } from '@/components/ui/AuthGuard'
import { AdminGuard } from '@/components/ui/AdminGuard'
import { Shell } from '@/components/gg/Shell'
import { AdminShell } from '@/components/admin/AdminShell'
import { AdminDashboard } from '@/components/admin/AdminDashboard'
import { AdminClientView } from '@/components/admin/AdminClientView'
import { AdminSettings } from '@/pages/AdminSettings'
import { Landing } from '@/pages/Landing'
import { Login } from '@/pages/Login'
import { Signup } from '@/pages/Signup'
import { Privacy } from '@/pages/legal/Privacy'
import { Terms } from '@/pages/legal/Terms'
import { Contact } from '@/pages/legal/Contact'
import { SwipeDeck } from '@/components/deck/SwipeDeck'
import { FeedPage } from '@/pages/feed/FeedPage'
import { FeedControlsPage } from '@/pages/feed/FeedControlsPage'
import { TopicsPage } from '@/pages/feed/TopicsPage'
import { PlacesPage } from '@/pages/feed/PlacesPage'
import { BreakingSettingsPage } from '@/pages/feed/BreakingSettingsPage'
import { RecordsSettingsPage } from '@/pages/feed/RecordsSettingsPage'
import { LibraryPage } from '@/pages/library/LibraryPage'
import { RecordPage } from '@/pages/record/RecordPage'
import { RecordPickPage } from '@/pages/record/RecordPickPage'
import { AlertsPage } from '@/pages/alerts/AlertsPage'
import { AlertDetailPage } from '@/pages/alerts/AlertDetailPage'
import { RecordFindsPage } from '@/pages/records/RecordFindsPage'
import { RecordFindPage } from '@/pages/records/RecordFindPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { ProfilePage } from '@/pages/settings/ProfilePage'
import { AchievementsPage } from '@/pages/settings/AchievementsPage'
import { NewsletterPage } from '@/pages/newsletter/NewsletterPage'
import { StoryPage } from '@/pages/story/StoryPage'
import { AffiliatePage } from '@/pages/affiliate/AffiliatePage'
import { ComingSoonPage } from '@/pages/ComingSoonPage'
import { Onboarding } from '@/pages/Onboarding'
import { useNudgeSync } from '@/hooks/useNudge'
import { OnboardingGuard } from '@/components/ui/OnboardingGuard'
import { lazy, Suspense } from 'react'
import { MediaKitPublic } from '@/pages/MediaKitPublic'
import { NewsletterSignup, NewsletterConfirm, NewsletterUnsubscribe } from '@/pages/newsletter/NewsletterPublic'
import { ReferralLanding } from '@/pages/ReferralLanding'
import { AdminAffiliates } from '@/components/admin/AdminAffiliates'
import { AdminOps } from '@/components/admin/AdminOps'

import './index.css'

const isNative = Capacitor.isNativePlatform()

// DEV-only portal preview (seeds mock data). Tree-shaken out of production builds.
const DevPortal = import.meta.env.DEV
  ? lazy(() => import('@/dev/DevPortal').then((m) => ({ default: m.DevPortal })))
  : null

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
})

function SwipeDeckRoute() {
  const navigate = useNavigate()
  // A build costs 12 seeds, so land the user on what they just paid for rather
  // than leaving them on the deck wondering whether it worked.
  return <SwipeDeck onStoryBuilt={(story) => navigate(`/app/story/${story.id}`)} />
}

function AppRoutes() {
  useAuthInit()
  // Registration triggers the OS permission prompt, so it waits for a session
  // rather than ambushing a visitor on the landing page.
  usePush({ autoRegister: !!useAuthStore((s) => s.session) })
  // Re-arms the daily 9am local notification. A reboot or an app update can
  // clear pending notifications on some Android builds, so this must run every
  // session rather than only at first launch.
  useNudgeSync(!!useAuthStore((s) => s.session))

  return (
    <Routes>
      {/* Public. The landing page is marketing for the web only — inside the
          native shell it would be a wrapped website, so go straight to the
          app and let AuthGuard bounce to /login when there's no session. */}
      <Route path="/" element={isNative ? <Navigate to="/app" replace /> : <Landing />} />
      <Route path="/login" element={<Login />} />
      {/* Sign-ups happen on the website only. In the native app these routes
          (including referral links) go to sign in. */}
      <Route path="/signup" element={isNative ? <Navigate to="/login" replace /> : <Signup />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/r/:code" element={isNative ? <Navigate to="/login" replace /> : <ReferralLanding />} />
      <Route path="/kit/:token" element={<MediaKitPublic />} />
      {/* The newsletter's public side: signup, confirm, unsubscribe (Resend handoff 3.3). */}
      <Route path="/n/:handle" element={<NewsletterSignup />} />
      <Route path="/c/:token" element={<NewsletterConfirm />} />
      <Route path="/u/:token" element={<NewsletterUnsubscribe />} />

      {/* Forced setup. Lives inside AuthGuard but outside the shell, because a
          client with no regions or topics has nothing for the nav to point at. */}
      <Route
        path="/app/onboarding"
        element={
          <AuthGuard>
            <OnboardingGuard>
              <Onboarding />
            </OnboardingGuard>
          </AuthGuard>
        }
      />

      {/* Protected app — lives at /app/* */}
      <Route
        path="/app"
        element={
          <AuthGuard>
            <OnboardingGuard>
              <Shell />
            </OnboardingGuard>
          </AuthGuard>
        }
      >
        {/* The route map Brian's app needs (docs/PORT-ROUTES.md). Each page
            file owns one screen and declares its chrome with <PageChrome>. */}
        <Route index element={<FeedPage />} />
        <Route path="feed-controls" element={<FeedControlsPage />} />
        <Route path="feed/topics" element={<TopicsPage />} />
        <Route path="feed/places" element={<PlacesPage />} />
        <Route path="feed/breaking" element={<BreakingSettingsPage />} />
        <Route path="feed/records" element={<RecordsSettingsPage />} />
        <Route path="library" element={<LibraryPage />} />
        <Route path="record" element={<RecordPage />} />
        <Route path="record/pick" element={<RecordPickPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="alerts/:id" element={<AlertDetailPage />} />
        <Route path="records" element={<RecordFindsPage />} />
        <Route path="records/:id" element={<RecordFindPage />} />
        <Route path="story/:id" element={<StoryPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="achievements" element={<AchievementsPage />} />
        <Route path="coins" element={<Navigate to="/app/achievements" replace />} />
        <Route path="newsletter/*" element={<NewsletterPage />} />
        <Route path="affiliate" element={<AffiliatePage />} />
        <Route path="soon/:what" element={<ComingSoonPage />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>

      {/* Super admin — lives at /admin/* */}
      <Route
        path="/admin"
        element={
          <AuthGuard>
            <AdminGuard>
              <AdminShell />
            </AdminGuard>
          </AuthGuard>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="affiliates" element={<AdminAffiliates />} />
        <Route path="ops" element={<AdminOps />} />
        <Route path="client/:clientId" element={<AdminClientView />} />
      </Route>

      {/* DEV-only portal preview with mock data */}
      {DevPortal && (
        <>
        <Route path="/dev/onboarding" element={<Onboarding />} />
        <Route
          path="/dev"
          element={
            <Suspense fallback={<div className="loading-screen"><div className="spinner" /></div>}>
              <DevPortal />
            </Suspense>
          }
        >
          <Route index element={<SwipeDeckRoute />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="story/:id" element={<StoryPage />} />
          <Route path="library" element={<LibraryPage />} />
          <Route path="coins" element={<AchievementsPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>
        </>
      )}
    </Routes>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  )
}
